/**
 * EventTimeline Component
 * WP-RETIRE-8: Thin strip visualization below the heatmap
 *
 * Shows events positioned along a timeline matching the heatmap's year range.
 * - Income events: green bars/markers
 * - Expense events: red bars/markers
 * - Lump sums: thin vertical line at their year
 * - Cash flows: horizontal bar spanning year to endYear
 * - Hover shows event name and amount
 *
 * Height: ~36px
 */

import { useMemo } from 'react';
import { centsToDollars, formatCurrency } from '@/utils/format';
import type { RetirementEvent } from '@/types/retirement';

interface EventTimelineProps {
  events: RetirementEvent[];
  /** First year displayed in the heatmap (calendar year) */
  startYear: number;
  /** Last year displayed in the heatmap (calendar year) */
  endYear: number;
}

export default function EventTimeline({
  events,
  startYear,
  endYear,
}: EventTimelineProps) {
  const totalYears = endYear - startYear;

  // Filter events that overlap with the visible year range
  const visibleEvents = useMemo(() => {
    if (totalYears <= 0) return [];

    return events.filter((event) => {
      const eventEnd =
        event.eventType === 'cash_flow' && event.endYear != null
          ? event.endYear
          : event.year;
      // Event overlaps if it starts before endYear and ends on or after startYear
      return event.year <= endYear && eventEnd >= startYear;
    });
  }, [events, startYear, endYear, totalYears]);

  if (visibleEvents.length === 0 || totalYears <= 0) {
    return null;
  }

  /**
   * Calculate horizontal position as a percentage of the timeline width.
   * Clamps to [0, 100] for events that partially extend beyond the range.
   */
  function yearToPercent(year: number): number {
    const pct = ((year - startYear) / totalYears) * 100;
    return Math.max(0, Math.min(100, pct));
  }

  return (
    <div className="mt-3" aria-label="Event timeline">
      {/* Label */}
      <div className="text-xs text-gray-500 mb-1 pl-24">Events</div>

      {/* Timeline strip */}
      <div className="flex">
        {/* Spacer to align with heatmap row label column (w-24) */}
        <div className="w-24 flex-shrink-0" />

        {/* Timeline area */}
        <div className="flex-1 relative h-9 bg-gray-50 rounded border border-gray-200">
          {visibleEvents.map((event) => {
            const isIncome = event.amountCents >= 0;
            const displayAmount = formatCurrency(
              Math.abs(centsToDollars(event.amountCents))
            );

            if (event.eventType === 'lump_sum') {
              // Thin vertical line at the event year
              const leftPct = yearToPercent(event.year);
              return (
                <div
                  key={event.id}
                  className="absolute top-1 bottom-1 group"
                  style={{ left: `${leftPct}%` }}
                  title={`${event.name}: ${isIncome ? '+' : '-'}${displayAmount} (${event.year})`}
                >
                  <div
                    className={`w-0.5 h-full rounded-full ${
                      isIncome ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  {/* Hover tooltip */}
                  <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                    {event.name}: {isIncome ? '+' : '-'}{displayAmount}
                    <br />
                    {event.year}
                  </div>
                </div>
              );
            }

            // Cash flow: horizontal bar from year to endYear
            const eventEndYear = event.endYear ?? event.year;
            const leftPct = yearToPercent(event.year);
            const rightPct = yearToPercent(eventEndYear);
            const widthPct = Math.max(rightPct - leftPct, 1); // Min 1% width for visibility

            return (
              <div
                key={event.id}
                className={`absolute top-1.5 h-5 rounded group cursor-default ${
                  isIncome
                    ? 'bg-green-200 border border-green-400'
                    : 'bg-red-200 border border-red-400'
                }`}
                style={{
                  left: `${leftPct}%`,
                  width: `${widthPct}%`,
                }}
                title={`${event.name}: ${isIncome ? '+' : '-'}${displayAmount}/yr (${event.year}-${eventEndYear})`}
              >
                {/* Event name (shown if bar is wide enough) */}
                <span
                  className={`block truncate px-1 text-xs leading-5 ${
                    isIncome ? 'text-green-800' : 'text-red-800'
                  }`}
                >
                  {event.name}
                </span>

                {/* Hover tooltip */}
                <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap z-10">
                  {event.name}: {isIncome ? '+' : '-'}{displayAmount}/yr
                  <br />
                  {event.year} - {eventEndYear}
                </div>
              </div>
            );
          })}

          {/* Year markers at edges */}
          <div className="absolute bottom-0 left-0.5 text-[10px] text-gray-400">
            {startYear}
          </div>
          <div className="absolute bottom-0 right-0.5 text-[10px] text-gray-400">
            {endYear}
          </div>
        </div>
      </div>
    </div>
  );
}
